import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import SEO from '../components/SEO';
import ScrollReveal from '../components/ScrollReveal';
import API_BASE_URL from '../config';
import features from '../features';

/* ── Animated counter ── */
function AnimatedCounter({ target, suffix }) {
  var [count, setCount] = useState(0);
  var [started, setStarted] = useState(false);
  var ref = useRef(null);
  var numTarget = parseInt(target, 10);

  useEffect(function () {
    var el = ref.current;
    if (!el) return;
    var observer = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) { setStarted(true); observer.disconnect(); }
    }, { threshold: 0.5 });
    observer.observe(el);
    return function () { observer.disconnect(); };
  }, []);

  useEffect(function () {
    if (!started || isNaN(numTarget)) return;
    var duration = 1400;
    var startTime = null;
    function step(ts) {
      if (!startTime) startTime = ts;
      var progress = Math.min((ts - startTime) / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * numTarget));
      if (progress < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }, [numTarget, started]);

  return <span ref={ref}>{count}{suffix || ''}</span>;
}

/* ── Typewriter effect for hero ── */
function Typewriter({ words, speed, pause }) {
  var [text, setText] = useState('');
  var [wordIdx, setWordIdx] = useState(0);
  var [charIdx, setCharIdx] = useState(0);
  var [deleting, setDeleting] = useState(false);

  useEffect(function () {
    var current = words[wordIdx];
    var timeout;
    if (!deleting && charIdx < current.length) {
      timeout = setTimeout(function () {
        setText(current.slice(0, charIdx + 1));
        setCharIdx(charIdx + 1);
      }, speed || 80);
    } else if (!deleting && charIdx === current.length) {
      timeout = setTimeout(function () { setDeleting(true); }, pause || 2000);
    } else if (deleting && charIdx > 0) {
      timeout = setTimeout(function () {
        setText(current.slice(0, charIdx - 1));
        setCharIdx(charIdx - 1);
      }, 40);
    } else if (deleting && charIdx === 0) {
      setDeleting(false);
      setWordIdx(function (i) { return (i + 1) % words.length; });
    }
    return function () { clearTimeout(timeout); };
  }, [charIdx, deleting, wordIdx, words, speed, pause]);

  return (
    <span className="ma2-typewriter">
      {text}<span className="ma2-cursor">|</span>
    </span>
  );
}

/* ── FAQ Accordion ── */
function FaqItem({ q, a }) {
  var [open, setOpen] = useState(false);
  return (
    <div className={'ma2-faq-item' + (open ? ' ma2-faq-item--open' : '')}>
      <button className="ma2-faq-q" onClick={function () { setOpen(!open); }} type="button" aria-expanded={open}>
        <span>{q}</span>
        <svg className="ma2-faq-chevron" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      <div className="ma2-faq-a" style={{ maxHeight: open ? '400px' : '0' }}>
        <p>{a}</p>
      </div>
    </div>
  );
}

/* ── Data ── */
var showcaseItems = [
  {
    label: 'AI Copywriting',
    title: 'Describe the outcome. Get polished copy back.',
    desc: 'Give it a one-line brief and your brand voice. The Marketing Agent writes email sequences, social posts, ad copy, and landing pages — all consistent with your tone. You review, refine, and ship.',
    visual: 'copy',
  },
  {
    label: 'Campaign Planning',
    title: 'From idea to full campaign in minutes, not weeks.',
    desc: 'Turn a single sentence into a complete campaign brief with messaging pillars, channel strategy, budget allocation, and a content calendar. The agent handles the planning so you can focus on decisions.',
    visual: 'campaign',
  },
  {
    label: 'Performance Analytics',
    title: 'Interact with the data — not just dashboards.',
    desc: 'Connect your channels and ask questions in plain English. Which ad set is bleeding budget? What content drove the most signups last month? Get answers with interactive charts, not static reports.',
    visual: 'analytics',
  },
];

/* ── Animated Blob Icon ── */
function AnimatedBlobIcon({ icon, index }) {
  /* Each icon gets a slightly different animation delay for organic feel */
  var blobPaths = [
    'M44.5,2.2C54.9,5.5,60.3,18.5,63.2,30.1C66.1,41.7,66.5,51.9,60.5,58.8C54.5,65.7,42.1,69.3,31.3,67.3C20.5,65.3,11.3,57.7,5.9,48.1C0.5,38.5,-1.1,26.9,3.5,18.3C8.1,9.7,18.9,4.1,29.1,1.5C39.3,-1.1,34.1,-1.1,44.5,2.2Z',
    'M42.8,1.5C52.4,5.8,57.2,19.2,60.8,31.2C64.4,43.2,66.8,53.8,61.2,60.2C55.6,66.6,42,68.8,30.8,67.2C19.6,65.6,10.8,60.2,5.4,51.2C0,42.2,-1.8,29.6,2.4,20.4C6.6,11.2,16.8,5.4,27.4,2.2C38,-1,33.2,-2.8,42.8,1.5Z',
    'M46.2,3.8C55.2,8.4,62.8,18.6,64.2,30.2C65.6,41.8,60.8,54.8,52.8,61.4C44.8,68,33.6,68.2,24,64.6C14.4,61,6.4,53.6,2.6,44.2C-1.2,34.8,-0.8,23.4,4,15.2C8.8,7,18,2,27.6,0.6C37.2,-0.8,37.2,-0.8,46.2,3.8Z',
  ];
  var blobPath = blobPaths[index % blobPaths.length];
  var delay = (index * 0.4) + 's';

  var icons = {
    envelope: (
      <path d="M4 6l8 5 8-5M4 6h16v12H4z" stroke="var(--text-heading)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    ),
    social: (
      <g stroke="var(--text-heading)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none">
        <rect x="5" y="3" width="14" height="18" rx="3" />
        <circle cx="12" cy="12" r="3" />
        <line x1="12" y1="20" x2="12" y2="21" />
      </g>
    ),
    target: (
      <g stroke="var(--text-heading)" strokeWidth="1.8" fill="none">
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="5" />
        <circle cx="12" cy="12" r="1.5" fill="var(--text-heading)" />
      </g>
    ),
    chart: (
      <g stroke="var(--text-heading)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none">
        <line x1="5" y1="20" x2="5" y2="12" />
        <line x1="9" y1="20" x2="9" y2="8" />
        <line x1="13" y1="20" x2="13" y2="14" />
        <line x1="17" y1="20" x2="17" y2="5" />
        <polyline points="3,6 8,4 13,9 19,3" />
      </g>
    ),
    flask: (
      <g stroke="var(--text-heading)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none">
        <path d="M9 3h6M10 3v6l-5 8.5a1 1 0 00.85 1.5h12.3a1 1 0 00.85-1.5L14 9V3" />
        <line x1="8.5" y1="14" x2="15.5" y2="14" />
      </g>
    ),
    clipboard: (
      <g stroke="var(--text-heading)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none">
        <path d="M16 4h2a1 1 0 011 1v14a1 1 0 01-1 1H6a1 1 0 01-1-1V5a1 1 0 011-1h2" />
        <rect x="8" y="2" width="8" height="4" rx="1" />
        <line x1="9" y1="12" x2="15" y2="12" />
        <line x1="9" y1="16" x2="13" y2="16" />
      </g>
    ),
  };

  return (
    <div className="ma2-blob-icon" style={{ animationDelay: delay }}>
      <svg className="ma2-blob-svg" viewBox="0 0 68 70" xmlns="http://www.w3.org/2000/svg">
        <path d={blobPath} fill="var(--primary)" opacity="0.13" className="ma2-blob-path" style={{ animationDelay: delay }} />
        <path d={blobPath} fill="none" stroke="var(--primary)" strokeWidth="1" opacity="0.25" className="ma2-blob-path" style={{ animationDelay: delay }} />
      </svg>
      <svg className="ma2-blob-icon-fg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        {icons[icon]}
      </svg>
    </div>
  );
}

var useCases = [
  { icon: 'envelope', title: 'Email sequences', desc: 'Generate multi-part drip campaigns with subject lines, preview text, and body copy tuned for each stage of the funnel.' },
  { icon: 'social', title: 'Social content', desc: 'Create platform-native posts for X, LinkedIn, Instagram, and TikTok — with hashtags, hooks, and optimal posting times.' },
  { icon: 'target', title: 'Ad copy & creatives', desc: 'Write Google, Meta, and LinkedIn ads with multiple headline and description variants ready for A/B testing.' },
  { icon: 'chart', title: 'Weekly reports', desc: 'Schedule automated performance digests that pull metrics from all your channels into one clear summary.' },
  { icon: 'flask', title: 'A/B test variants', desc: 'Automatically generate test variations and get data-driven recommendations on which version wins.' },
  { icon: 'clipboard', title: 'Strategy briefs', desc: 'Turn a one-line idea into a full campaign brief with messaging, channels, timeline, and budget allocation.' },
];

var testimonials = [
  { quote: 'We replaced 3 marketing tools with the Marketing Agent. Our content output tripled and ad spend dropped 40%.', name: 'Priya S.', role: 'Head of Growth, SaaS Startup' },
  { quote: 'The AI writes better first drafts than most junior copywriters. We just edit and ship. Turnaround went from days to hours.', name: 'Marcus L.', role: 'Marketing Director, E-commerce' },
  { quote: 'Campaign planning used to take our team a full week. Now we go from idea to launch in 2 days.', name: 'Aisha K.', role: 'Founder, D2C Brand' },
];

var pricingPlans = [
  { name: 'Starter', price: '49', period: '/mo', desc: 'For solo founders and small teams getting started.', features: ['500 AI generations/mo', '3 brand voices', 'Email & social copy', 'Basic analytics dashboard', 'Email support'], cta: 'Start Free Trial', highlight: false },
  { name: 'Growth', price: '149', period: '/mo', desc: 'For growing teams that need serious output.', features: ['Unlimited AI generations', '10 brand voices', 'Full campaign planning', 'A/B test recommendations', 'Advanced analytics', 'Priority support'], cta: 'Start Free Trial', highlight: true },
  { name: 'Scale', price: '399', period: '/mo', desc: 'For agencies and enterprises running at volume.', features: ['Everything in Growth', 'Unlimited brand voices', 'Custom AI training on your data', 'API access', 'Dedicated account manager', 'SSO & team management'], cta: 'Contact Sales', highlight: false },
];

var faqs = [
  { q: 'How is this different from ChatGPT or other AI writing tools?', a: 'Most AI tools give you text. The Marketing Agent gives you complete campaigns — copy, strategy, scheduling, and analytics in one workflow. It understands marketing context, not just language.' },
  { q: 'Can it match my brand voice?', a: 'Yes. Upload your brand guidelines, past content, or just describe your tone. The agent learns your voice and applies it consistently across every channel and format.' },
  { q: 'What channels does it support?', a: 'Email, social media (X, LinkedIn, Instagram, TikTok), Google Ads, Meta Ads, landing pages, blog posts, and more. New channels are added regularly.' },
  { q: 'Do I need technical skills to use it?', a: 'Not at all. Everything works through natural language. Describe what you want in plain English and the agent handles the rest. No prompts to memorize, no complex setup.' },
  { q: 'Can I try it before committing?', a: 'Every plan includes a 14-day free trial with full access. No credit card required to start.' },
];

/* ── Showcase visual illustrations (SVG) ── */
function ShowcaseVisual({ type }) {
  if (type === 'copy') {
    return (
      <div className="ma2-visual-terminal">
        <div className="ma2-vt-bar">
          <div className="ma2-vt-dots"><span className="ma2-vt-dot ma2-vt-dot--r"></span><span className="ma2-vt-dot ma2-vt-dot--y"></span><span className="ma2-vt-dot ma2-vt-dot--g"></span></div>
          <span className="ma2-vt-title">Brand Voice</span>
        </div>
        <div className="ma2-vt-body">
          <div className="ma2-vt-line"><span className="ma2-vt-tag">Subject</span> Your free trial is about to get even better</div>
          <div className="ma2-vt-line"><span className="ma2-vt-tag">Preview</span> We added 3 features you asked for...</div>
          <div className="ma2-vt-divider"></div>
          <div className="ma2-vt-line ma2-vt-line--muted">Hey [first_name],</div>
          <div className="ma2-vt-line ma2-vt-line--muted">Remember that feature you requested? It just shipped.</div>
          <div className="ma2-vt-line ma2-vt-line--muted">Here's what's new this week:</div>
          <div className="ma2-vt-line ma2-vt-line--accent">✓ Smart scheduling &nbsp; ✓ Team dashboards &nbsp; ✓ API v2</div>
        </div>
      </div>
    );
  }
  if (type === 'campaign') {
    return (
      <div className="ma2-visual-terminal">
        <div className="ma2-vt-bar">
          <div className="ma2-vt-dots"><span className="ma2-vt-dot ma2-vt-dot--r"></span><span className="ma2-vt-dot ma2-vt-dot--y"></span><span className="ma2-vt-dot ma2-vt-dot--g"></span></div>
          <span className="ma2-vt-title">Campaign Planner</span>
        </div>
        <div className="ma2-vt-body">
          <div className="ma2-vt-line"><span className="ma2-vt-tag">Goal</span> Product Hunt launch — maximize signups</div>
          <div className="ma2-vt-divider"></div>
          <div className="ma2-vt-row"><span className="ma2-vt-pill">Week 1</span> Teaser campaign — build waitlist</div>
          <div className="ma2-vt-row"><span className="ma2-vt-pill">Week 2</span> Launch day — coordinated blast</div>
          <div className="ma2-vt-row"><span className="ma2-vt-pill">Week 3</span> Social proof — testimonials & metrics</div>
          <div className="ma2-vt-row"><span className="ma2-vt-pill">Week 4</span> Retargeting — convert stragglers</div>
        </div>
      </div>
    );
  }
  /* analytics */
  return (
    <div className="ma2-visual-terminal">
      <div className="ma2-vt-bar">
        <div className="ma2-vt-dots"><span className="ma2-vt-dot ma2-vt-dot--r"></span><span className="ma2-vt-dot ma2-vt-dot--y"></span><span className="ma2-vt-dot ma2-vt-dot--g"></span></div>
        <span className="ma2-vt-title">Analytics</span>
      </div>
      <div className="ma2-vt-body">
        <div className="ma2-vt-metrics">
          <div className="ma2-vt-metric"><span className="ma2-vt-metric-val">2,847</span><span className="ma2-vt-metric-label">Signups</span><span className="ma2-vt-metric-delta ma2-vt-metric-delta--up">+34%</span></div>
          <div className="ma2-vt-metric"><span className="ma2-vt-metric-val">$1.24</span><span className="ma2-vt-metric-label">CPA</span><span className="ma2-vt-metric-delta ma2-vt-metric-delta--up">-18%</span></div>
          <div className="ma2-vt-metric"><span className="ma2-vt-metric-val">4.2%</span><span className="ma2-vt-metric-label">CTR</span><span className="ma2-vt-metric-delta ma2-vt-metric-delta--up">+12%</span></div>
        </div>
        <div className="ma2-vt-chart">
          <svg viewBox="0 0 280 60" fill="none" style={{ width: '100%', height: 60 }}>
            <polyline points="0,50 30,42 60,45 90,30 120,35 150,20 180,22 210,12 240,15 270,5" stroke="var(--primary)" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            <linearGradient id="cg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="var(--primary)" stopOpacity="0.15" /><stop offset="100%" stopColor="var(--primary)" stopOpacity="0" /></linearGradient>
            <polygon points="0,50 30,42 60,45 90,30 120,35 150,20 180,22 210,12 240,15 270,5 270,60 0,60" fill="url(#cg)" />
          </svg>
        </div>
      </div>
    </div>
  );
}


/* ── Main Component ── */
function MarketingAgent() {
  var navigate = useNavigate();
  var [dashboardUrl, setDashboardUrl] = useState(null);

  useEffect(function() {
    if (!features.agentEnabled) return;
    var token = localStorage.getItem('agent_token');
    if (!token) return;
    fetch(API_BASE_URL + '/api/projects', {
      headers: { Authorization: 'Bearer ' + token },
    }).then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.projects && d.projects.length > 0) {
          setDashboardUrl('/agent/dashboard/' + d.projects[0].id);
        } else {
          setDashboardUrl('/agent/onboarding');
        }
      }).catch(function() {});
  }, []);

  function handleCta(e) {
    e.preventDefault();
    if (!features.agentEnabled) { navigate('/contact'); return; }
    if (dashboardUrl) { navigate(dashboardUrl); } else { navigate('/agent'); }
  }

  var ctaLabel = !features.agentEnabled ? 'Get Early Access' : (dashboardUrl ? 'Go to Dashboard' : 'Start Free Trial');

  return (
    <div className="ma2-page">
      <SEO
        title="Marketing Agent — AI-Powered Marketing Automation | Anthroper"
        description="Generate copy, plan campaigns, analyze performance, and optimize spend. The Marketing Agent handles your entire marketing workflow."
        path="/products/marketing-agent"
      />

      {/* ═══ Hero ═══ */}
      <section className="ma2-hero">
        <div className="ma2-hero-glow"></div>
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <ScrollReveal>
            <Link to="/products" className="back-link--pill">← All Products</Link>
          </ScrollReveal>
          <ScrollReveal delay={50}>
            <p className="ma2-hero-badge">
              <span className="ma2-hero-badge-dot"></span>
              Now in Early Access
            </p>
          </ScrollReveal>
          <ScrollReveal delay={100}>
            <h1 className="ma2-hero-title">
              Hand off your marketing.<br />
              <span className="ma2-hero-accent">
                Get <Typewriter words={['campaigns', 'ad copy', 'analytics', 'strategy', 'content']} speed={70} pause={1800} /> back.
              </span>
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={200}>
            <p className="ma2-hero-sub">
              Spend less time writing, scheduling, and analyzing. The Marketing Agent handles your entire workflow — from brief to published campaign — while you focus on the decisions that matter.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={300}>
            <div className="ma2-hero-actions">
              <a href="#pricing" className="btn btn-primary btn-lg btn-glow" onClick={handleCta}>{ctaLabel}</a>
              <a href="#showcase" className="btn btn-outline btn-lg">See How It Works</a>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ═══ Stats Bar ═══ */}
      <section className="ma2-stats">
        <div className="container">
          <div className="ma2-stats-grid">
            {[
              { value: '10', suffix: 'x', label: 'Faster Content Creation' },
              { value: '40', suffix: '%', label: 'Lower Ad Spend' },
              { value: '85', suffix: '%', label: 'Time Saved Weekly' },
              { value: '3', suffix: 'x', label: 'More Campaign Output' },
            ].map(function (s, i) {
              return (
                <ScrollReveal key={s.label} delay={i * 100}>
                  <div className="ma2-stat">
                    <span className="ma2-stat-value"><AnimatedCounter target={s.value} suffix={s.suffix} /></span>
                    <span className="ma2-stat-label">{s.label}</span>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ Showcase — alternating text + visual ═══ */}
      <section className="ma2-showcase" id="showcase">
        {showcaseItems.map(function (item, i) {
          var reversed = i % 2 === 1;
          return (
            <div key={item.label} className={'ma2-showcase-row' + (reversed ? ' ma2-showcase-row--reversed' : '') + (i % 2 === 0 ? '' : ' ma2-showcase-row--tinted')}>
              <div className="container">
                <div className="ma2-showcase-inner">
                  <ScrollReveal delay={0}>
                    <div className="ma2-showcase-text">
                      <span className="ma2-showcase-label">{item.label}</span>
                      <h2 className="ma2-showcase-title">{item.title}</h2>
                      <p className="ma2-showcase-desc">{item.desc}</p>
                    </div>
                  </ScrollReveal>
                  <ScrollReveal delay={150}>
                    <div className="ma2-showcase-visual">
                      <ShowcaseVisual type={item.visual} />
                    </div>
                  </ScrollReveal>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      {/* ═══ Use Cases Grid ═══ */}
      <section className="ma2-section">
        <div className="container">
          <ScrollReveal>
            <div className="ma2-section-header">
              <h2 className="ma2-section-title">Everything you need to ship campaigns</h2>
              <p className="ma2-section-sub">One agent replaces your copywriter, analyst, and campaign manager.</p>
            </div>
          </ScrollReveal>
          <div className="ma2-usecases-grid">
            {useCases.map(function (uc, i) {
              return (
                <ScrollReveal key={uc.title} delay={i * 60}>
                  <div className="ma2-usecase-card">
                    <AnimatedBlobIcon icon={uc.icon} index={i} />
                    <h3>{uc.title}</h3>
                    <p>{uc.desc}</p>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ How It Works ═══ */}
      <section className="ma2-section ma2-section--tinted">
        <div className="container">
          <ScrollReveal>
            <div className="ma2-section-header">
              <h2 className="ma2-section-title">How it works</h2>
              <p className="ma2-section-sub">Three steps. No learning curve.</p>
            </div>
          </ScrollReveal>
          <div className="ma2-steps">
            {[
              { num: '01', title: 'Describe what you need', desc: 'Tell the agent your goal in plain English. "Launch a Black Friday campaign for our SaaS product targeting startup founders." That\'s it.' },
              { num: '02', title: 'The agent does the work', desc: 'It writes copy, plans the timeline, generates ad variants, and builds a tracking plan. Watch in real time or walk away.' },
              { num: '03', title: 'You review and ship', desc: 'Everything lands in your dashboard ready to review. Edit, approve, and publish — or ask the agent to iterate.' },
            ].map(function (step, i) {
              return (
                <ScrollReveal key={step.num} delay={i * 120}>
                  <div className="ma2-step">
                    <span className="ma2-step-num">{step.num}</span>
                    <div className="ma2-step-content">
                      <h3>{step.title}</h3>
                      <p>{step.desc}</p>
                    </div>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ Savings Comparison ═══ */}
      <section className="ma2-section">
        <div className="container">
          <ScrollReveal>
            <div className="ma2-section-header">
              <h2 className="ma2-section-title">Save thousands every month</h2>
              <p className="ma2-section-sub">Companies using the Marketing Agent are cutting costs and shipping faster.</p>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={100}>
            <div className="products-savings">
              <div className="products-savings-col">
                <h3 className="products-savings-heading products-savings-heading--old">Without Anthroper</h3>
                <ul className="products-savings-list">
                  <li><span className="products-savings-item">Freelance copywriter</span><span className="products-savings-cost">$3,000/mo</span></li>
                  <li><span className="products-savings-item">Social media manager</span><span className="products-savings-cost">$4,500/mo</span></li>
                  <li><span className="products-savings-item">Analytics tools (3+)</span><span className="products-savings-cost">$500/mo</span></li>
                  <li><span className="products-savings-item">Campaign planning time</span><span className="products-savings-cost">40 hrs/mo</span></li>
                  <li className="products-savings-total"><span>Total</span><span>$8,000+/mo</span></li>
                </ul>
              </div>
              <div className="products-savings-vs">vs</div>
              <div className="products-savings-col">
                <h3 className="products-savings-heading products-savings-heading--new">With Anthroper</h3>
                <ul className="products-savings-list">
                  <li><span className="products-savings-item">Marketing Agent</span><span className="products-savings-cost products-savings-cost--highlight">$149/mo</span></li>
                  <li><span className="products-savings-item">Your time reviewing</span><span className="products-savings-cost products-savings-cost--highlight">5 hrs/mo</span></li>
                  <li className="products-savings-total products-savings-total--highlight"><span>Total</span><span>$149/mo</span></li>
                </ul>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ═══ Testimonials ═══ */}
      <section className="ma2-section ma2-section--tinted">
        <div className="container">
          <ScrollReveal>
            <div className="ma2-section-header">
              <h2 className="ma2-section-title">What teams are saying</h2>
            </div>
          </ScrollReveal>
          <div className="ma2-testimonials-grid">
            {testimonials.map(function (t, i) {
              return (
                <ScrollReveal key={t.name} delay={i * 100}>
                  <div className="ma2-testimonial">
                    <svg className="ma2-testimonial-quote-icon" width="32" height="32" viewBox="0 0 24 24" fill="var(--primary-muted)"><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" /><path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z" /></svg>
                    <p className="ma2-testimonial-text">{t.quote}</p>
                    <div className="ma2-testimonial-author">
                      <div className="ma2-testimonial-avatar">{t.name.charAt(0)}</div>
                      <div>
                        <span className="ma2-testimonial-name">{t.name}</span>
                        <span className="ma2-testimonial-role">{t.role}</span>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ Pricing ═══ */}
      <section className="ma2-section" id="pricing">
        <div className="container">
          <ScrollReveal>
            <div className="ma2-section-header">
              <h2 className="ma2-section-title">Simple, transparent pricing</h2>
              <p className="ma2-section-sub">Start free for 14 days. No credit card required.</p>
            </div>
          </ScrollReveal>
          <div className="products-pricing-grid">
            {pricingPlans.map(function (plan, i) {
              return (
                <ScrollReveal key={plan.name} delay={i * 80}>
                  <div className={'products-pricing-card' + (plan.highlight ? ' products-pricing-card--highlight' : '')}>
                    {plan.highlight && <span className="products-pricing-badge">Most Popular</span>}
                    <h3 className="products-pricing-name">{plan.name}</h3>
                    <div className="products-pricing-price">
                      <span className="products-pricing-dollar">$</span>
                      <span className="products-pricing-amount">{plan.price}</span>
                      <span className="products-pricing-period">{plan.period}</span>
                    </div>
                    <p className="products-pricing-desc">{plan.desc}</p>
                    <ul className="products-pricing-features">
                      {plan.features.map(function (f, fi) {
                        return <li key={fi}>✓ {f}</li>;
                      })}
                    </ul>
                    <a href="/agent" className={'btn ' + (plan.highlight ? 'btn-primary' : 'btn-outline') + ' btn-lg'} style={{ width: '100%', textAlign: 'center' }} onClick={handleCta}>{dashboardUrl ? 'Go to Dashboard' : plan.cta}</a>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <section className="ma2-section ma2-section--tinted">
        <div className="container">
          <ScrollReveal>
            <div className="ma2-section-header">
              <h2 className="ma2-section-title">Frequently asked questions</h2>
            </div>
          </ScrollReveal>
          <div className="ma2-faq-list">
            {faqs.map(function (f, i) {
              return (
                <ScrollReveal key={i} delay={i * 60}>
                  <FaqItem q={f.q} a={f.a} />
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ Bottom CTA ═══ */}
      <section className="ma2-section">
        <div className="container">
          <ScrollReveal>
            <div className="ma2-bottom-cta">
              <h2>Ready to put AI to work on your marketing?</h2>
              <p>Start your 14-day free trial. No credit card, no commitment.</p>
              <div className="ma2-bottom-cta-actions">
                <a href="/agent" className="btn btn-primary btn-lg btn-glow" onClick={handleCta}>{ctaLabel}</a>
                <Link to="/products" className="ma2-bottom-cta-link">← Back to All Products</Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}

export default MarketingAgent;
